//External
import React, { useEffect, useMemo, useRef, useState } from "react";
//Internal
import IconChevronDown from "../icons/icons-chevron-down";
import FilterDropdownPanel, { FilterEmptyOptions } from "./FilterDropdownPanel";
import useUnavailableOptions from "./useUnavailableOptions";
import { countMatchingVehicles } from "./availabilityPool";

/*******************
@purpose : white checkmark used inside a checked/selected box
@Author : INIC
******************/
const CheckIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 5L4.2 8.2L11 1.4" stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/*******************
@Purpose : Reusable multi-select filter dropdown (Categories, Brands,
           Colors, ...) - a "Select All" row, a live "Show N cars" count
           for the in-progress selection, and a Reset that clears the draft
           selection entirely. Selections only take effect on the results
           grid once "Show N cars" is clicked - checking boxes edits a
           local draft first. Both the count and the zero-result option
           hiding are computed instantly off the shared availability pool -
           no network request happens on open or on each checkbox click.
@Parameter : {label, optionList, selectedValues, setSelectedValues, filterKey,
              getOptionValue, getOptionLabel, getOptionSwatch, otherFilters,
              pool}
  - filterKey: the availability-pool filters key this list maps to
    (e.g. "categorySlugs", "brandSlugs", "colorIds")
  - getOptionValue/getOptionLabel: how to read the value/label off an option
    object - defaults to Categories/Brands' `{slug, title}` shape; Colors
    passes `(c) => c.id` / `(c) => c.name` since its shape differs
  - getOptionSwatch: optional - when provided, renders a small color dot
    before each row's label (and a rainbow dot on "Select All"). Used by
    Colors only; Categories/Brands leave this unset.
  - otherFilters: the other already-wired real filters' current values
    (e.g. { categorySlugs: selectedCategories } when this instance is
    Brands), merged into the count so it reflects combined filtering, not
    just this dropdown's own selection
  - pool: vehicles already available for the current location+dates (see
    useAvailabilityPool at the page level)
@Author : INIC
******************/
function MultiSelectFilterDropdown({
  label,
  optionList,
  selectedValues,
  setSelectedValues,
  filterKey,
  getOptionValue = (option) => option.slug,
  getOptionLabel = (option) => option.title,
  getOptionSwatch,
  otherFilters = {},
  pool,
  colorList,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftValues, setDraftValues] = useState(selectedValues);
  const containerRef = useRef();

  const optionAvailabilityInputs = useMemo(
    () => (optionList || []).map((option) => {
      const value = getOptionValue(option);
      return { key: value, values: [value] };
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionList]
  );

  // Options that would show 0 vehicles if picked on their own (given every
  // other already-active filter) - hidden below rather than left clickable
  // as a dead end. An option the user already has checked stays visible
  // even if it now computes as unavailable, so it can still be unchecked.
  const unavailableValues = useUnavailableOptions({
    pool,
    filterKey,
    options: optionAvailabilityInputs,
    otherFilters,
    colorList,
  });

  const visibleOptionList = useMemo(
    () =>
      (optionList || []).filter((option) => {
        const value = getOptionValue(option);
        return !unavailableValues.has(value) || draftValues.includes(value);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionList, unavailableValues, draftValues]
  );

  const allValues = useMemo(
    () => visibleOptionList.map(getOptionValue),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleOptionList]
  );

  const isAllSelected =
    allValues.length > 0 && allValues.every((value) => draftValues.includes(value));
  const isSomeSelected = draftValues.length > 0 && !isAllSelected;

  /*******************
  @purpose : start each open with a fresh draft copied from the last applied
             selection, so re-opening doesn't carry over an abandoned edit
  @Author : INIC
  ******************/
  useEffect(() => {
    if (isOpen) {
      setDraftValues(selectedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // "Show N cars" for the in-progress draft (combined with the other
  // already-wired filters) - recomputed instantly on every keystroke/click,
  // straight off the pool.
  const previewCount = countMatchingVehicles(pool, { ...otherFilters, [filterKey]: draftValues }, colorList);

  const toggleValue = (value) => {
    setDraftValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleSelectAll = () => {
    setDraftValues(isAllSelected ? [] : allValues);
  };

  const handleReset = () => {
    setDraftValues([]);
  };

  const handleApply = () => {
    setSelectedValues(draftValues);
    setIsOpen(false);
  };

  /*******************
  @purpose : the plain label when nothing's picked, the single picked
             option's name, or "FirstPicked + N" for multiple - based on the
             last *applied* selection, not the in-progress draft
  @Author : INIC
  ******************/
  const triggerLabel = useMemo(() => {
    if (!selectedValues.length) return label;

    const selectedInOrder = (optionList || []).filter((option) =>
      selectedValues.includes(getOptionValue(option))
    );
    if (!selectedInOrder.length) return label;
    if (selectedInOrder.length === 1) {
      return getOptionLabel(selectedInOrder[0]) || label;
    }
    return `${getOptionLabel(selectedInOrder[0])} + ${selectedInOrder.length - 1}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues, optionList, label]);

  const isDraftEmpty = draftValues.length === 0;

  return (
    <div className="filter-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`filter-dropdown-trigger ${selectedValues.length ? "has-selection" : ""} ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="filter-dropdown-trigger-label">{triggerLabel}</span>
        <IconChevronDown className={`reservation-chevron ${isOpen ? "is-open" : ""}`} />
      </button>

      <FilterDropdownPanel
        isOpen={isOpen}
        anchorRef={containerRef}
        onOutsideClose={() => setIsOpen(false)}
        className="filter-dropdown-panel--multiselect"
      >
        <div className="filter-options-list">
          {/* Plain button instead of <label><input type="checkbox" hidden/>...</label> -
              the hidden input was position:absolute, and clicking a row near the
              bottom of this scrollable list made the browser's native
              focus-follows-click behavior scroll the container to "reveal" it,
              nudging the whole list up. A button needs no focusable native
              input to hide, so there's nothing for the browser to scroll to. */}
          {visibleOptionList.length === 0 ? (
            <FilterEmptyOptions />
          ) : (
            <button
              type="button"
              className="filter-checkbox-row"
              role="checkbox"
              aria-checked={isSomeSelected ? "mixed" : isAllSelected}
              onClick={toggleSelectAll}
            >
              <span className={`filter-checkbox-box ${isAllSelected ? "is-checked" : ""} ${isSomeSelected ? "is-indeterminate" : ""}`}>
                {isAllSelected && <CheckIcon />}
                {isSomeSelected && <span className="filter-checkbox-dash" />}
              </span>
              {getOptionSwatch && <span className="filter-swatch-dot filter-swatch-dot--rainbow" />}
              <span className="filter-checkbox-label">{isAllSelected ? "Deselect All" : "Select All"}</span>
            </button>
          )}

          {visibleOptionList.map((option) => {
            const value = getOptionValue(option);
            const checked = draftValues.includes(value);
            return (
              <button
                key={value}
                type="button"
                className="filter-checkbox-row"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggleValue(value)}
              >
                <span className={`filter-checkbox-box ${checked ? "is-checked" : ""}`}>
                  {checked && <CheckIcon />}
                </span>
                {getOptionSwatch && (
                  <span
                    className="filter-swatch-dot"
                    style={{ backgroundColor: getOptionSwatch(option) }}
                  />
                )}
                <span className="filter-checkbox-label">{getOptionLabel(option)}</span>
              </button>
            );
          })}
        </div>

        <div className="filter-dropdown-footer">
          <button
            type="button"
            className="filter-reset-btn"
            disabled={isDraftEmpty}
            onClick={handleReset}
          >
            Reset
          </button>
          <button type="button" className="filter-apply-btn" onClick={handleApply}>
            {pool?.length
              ? `Show ${previewCount} ${previewCount === 1 ? "car" : "cars"}`
              : "Show cars"}
          </button>
        </div>
      </FilterDropdownPanel>
    </div>
  );
}

export default MultiSelectFilterDropdown;
